import classNames from 'classnames';
import React from 'react';
import type { DistributedPick } from 'type-fest';
import alertIcon from '../icons/non-prefilled/16x16/alert.svg';
import checkIcon from '../icons/non-prefilled/16x16/check.svg';
import clearIcon from '../icons/non-prefilled/16x16/clear.svg';
import type { ToastLevel } from '../ui/uiSlice';
import type { ButtonProps } from './Button';
import Button from './Button';
import SVGIcon from './SVGIcon';
import * as styles from './Toast.module.scss';

const DISMISS_BUTTON_ICON_SIZE = parseInt(styles.dismissButtonIconSize, 10);

type ToastButtonRender = (options: {
  onDismiss: () => void;
}) => React.ReactNode;
type ToastButtonProps = DistributedPick<ButtonProps, 'to' | 'newTab'> & {
  onClick?: () => unknown;
  text: string;
};

type ToastButton = ToastButtonRender | ToastButtonProps;

export const TOAST_PUNCTUATION = ['.', '?', '!', '…'] as const;
export type ToastPunctuation = (typeof TOAST_PUNCTUATION)[number];
export type ToastMessage = `${string}${ToastPunctuation}`;

type BaseToastProps = {
  icon?: React.ReactNode;
  hideIcon?: boolean;
};

// eslint-disable-next-line import/no-unused-modules -- used in stories
export type InformationToastProps = BaseToastProps & {
  title?: string;
  message: ToastMessage;
  level: ToastLevel;
  button?: never;
};

type ActionToastProps = BaseToastProps & {
  title: string;
  message?: ToastMessage;
  level?: ToastLevel;
  button: ToastButton;
};

export type ToastProps = InformationToastProps | ActionToastProps;

// eslint-disable-next-line import/no-unused-modules -- used in stories
export const isInformationToastProps = (
  props: ToastProps,
): props is InformationToastProps => {
  return !('button' in props) || props.button == null;
};

const LEVEL_ICONS: Record<ToastLevel, string> = {
  success: checkIcon,
  error: alertIcon,
  warning: alertIcon,
};

interface DismissButtonContainerProps {
  children: React.ReactNode;
  onDismiss: () => void;
}

const DismissButtonContainer = ({
  children,
  onDismiss,
}: DismissButtonContainerProps) => {
  return (
    <div className={styles.dismissButtonContainer}>
      <div>{children}</div>
      <Button className={styles.dismissButton} onClick={onDismiss}>
        <SVGIcon size={DISMISS_BUTTON_ICON_SIZE}>{clearIcon}</SVGIcon>
      </Button>
    </div>
  );
};

export const ToastButton = (
  props: Exclude<ButtonProps, 'variant' | 'size'>,
) => {
  return <Button {...props} variant="secondary" size="small" />;
};

const Toast = (
  props: ToastProps & {
    onDismiss: () => void;
  },
) => {
  const { message, button, onDismiss } = props;

  const buttonElement = (() => {
    if (button == null) return null;

    if (typeof button === 'function') {
      return button({ onDismiss });
    }

    return (
      <ToastButton
        {...button}
        onClick={() => {
          button.onClick?.();
          onDismiss();
        }}
      >
        {button.text}
      </ToastButton>
    );
  })();

  const { level } = props;
  const icon = props.icon ?? (level ? LEVEL_ICONS[level] : null);

  return (
    <div
      className={classNames(
        styles.toast,
        {
          [styles.isInformationToast]: isInformationToastProps(props),
        },
        level && styles[level],
      )}
    >
      {!props.hideIcon && icon != null && (
        <div className={styles.icon}>
          {icon == null || typeof icon === 'string' ? (
            <SVGIcon>{icon}</SVGIcon>
          ) : (
            icon
          )}
        </div>
      )}
      <div className={styles.main}>
        <div className={styles.content}>
          {props.title ? (
            <>
              <DismissButtonContainer onDismiss={onDismiss}>
                <div className={styles.title}>{props.title}</div>
              </DismissButtonContainer>
              {message && <div className={styles.message}>{message}</div>}
            </>
          ) : (
            <DismissButtonContainer onDismiss={onDismiss}>
              <div className={styles.message}>{message}</div>
            </DismissButtonContainer>
          )}
        </div>
        {buttonElement}
      </div>
    </div>
  );
};

export default Toast;
